import React, { useMemo, useCallback } from 'react'
import { Instances, Instance } from '@react-three/drei'
import { ThreeEvent } from '@react-three/fiber'
import { RigidBody, CuboidCollider } from '@react-three/rapier'
import { Vector3 } from 'three'
import type { Brick } from '@models/brick'
import { getBrickDimensions } from '@utils/brickGeometry'
import { groupBricksForInstancing, type BrickGroup } from '@utils/brickGrouping'
import { isDebugMode, isPhysicsDisabled } from '@utils/debug'
import { BrickStuds } from './BrickStuds'
import { BrickSockets } from './BrickSockets'
import { useBrickStore } from '@store/brickStore'

interface BrickInstancesProps {
  bricks: Map<string, Brick>
  onHover?: (point: Vector3, isTopSurface: boolean, hoveredBrick?: Brick) => void
  onPlaceBrick?: (event: ThreeEvent<MouseEvent>) => void
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void
  onPointerMoveForDrag?: (event: ThreeEvent<PointerEvent>) => void
  isPreviewShowing?: boolean
}

/**
 * BrickInstances Component
 *
 * Optimized brick rendering using THREE.js geometry instancing via @react-three/drei.
 * Groups bricks by type+color and renders each group as a single instanced mesh,
 * drastically reducing draw calls.
 *
 * Performance Benefits:
 * - Before: N draw calls for N bricks
 * - After: M draw calls for M unique type+color combinations
 * - Example: 10,000 bricks of same type/color = 1 draw call instead of 10,000
 *
 * Features:
 * - Uses drei's <Instances> and <Instance> components (R3F-friendly)
 * - Maintains all interaction features (hover, click, selection)
 * - Preserves physics colliders (one RigidBody per brick)
 * - Supports individual brick rotation via instance matrix
 * - Renders studs and sockets per instance
 * - Selection highlighting with emissive material
 *
 * Task 4.1.1 - Geometry instancing for identical bricks
 *
 * @component
 */
export const BrickInstances: React.FC<BrickInstancesProps> = ({
  bricks,
  onHover,
  onPlaceBrick,
  onPointerDown,
  onPointerMoveForDrag,
  isPreviewShowing = false,
}) => {
  // Group bricks by type and color for instancing
  const brickGroups = useMemo(() => groupBricksForInstancing(bricks), [bricks])

  // Get selection state
  const selectedBrickId = useBrickStore((state) => state.selectedBrickId)
  const setSelectedBrickId = useBrickStore((state) => state.setSelectedBrickId)

  const debugMode = isDebugMode()
  const physicsDisabled = isPhysicsDisabled() // Task 4.1.6

  return (
    <>
      {brickGroups.map((group) => (
        <BrickGroupInstances
          key={group.key}
          group={group}
          selectedBrickId={selectedBrickId}
          setSelectedBrickId={setSelectedBrickId}
          debugMode={debugMode}
          physicsDisabled={physicsDisabled}
          onHover={onHover}
          onPlaceBrick={onPlaceBrick}
          onPointerDown={onPointerDown}
          onPointerMoveForDrag={onPointerMoveForDrag}
          isPreviewShowing={isPreviewShowing}
        />
      ))}
    </>
  )
}

/**
 * BrickGroupInstances Component
 *
 * Renders a single group of bricks (same type+color) as instanced meshes.
 * Each brick gets its own Instance with custom position/rotation.
 */
interface BrickGroupInstancesProps {
  group: BrickGroup
  selectedBrickId: string | null
  setSelectedBrickId: (id: string | null) => void
  debugMode: boolean
  physicsDisabled: boolean
  onHover?: (point: Vector3, isTopSurface: boolean, hoveredBrick?: Brick) => void
  onPlaceBrick?: (event: ThreeEvent<MouseEvent>) => void
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void
  onPointerMoveForDrag?: (event: ThreeEvent<PointerEvent>) => void
  isPreviewShowing?: boolean
}

const BrickGroupInstances: React.FC<BrickGroupInstancesProps> = ({
  group,
  selectedBrickId,
  setSelectedBrickId,
  debugMode,
  physicsDisabled,
  onHover,
  onPlaceBrick,
  onPointerDown,
  onPointerMoveForDrag,
  isPreviewShowing,
}) => {
  const { width, height, length } = getBrickDimensions(group.type)
  const depth = length
  const colorString = `rgb(${group.color.r}, ${group.color.g}, ${group.color.b})`

  return (
    <>
      {/* Instanced meshes for visual rendering */}
      <Instances castShadow receiveShadow>
        <boxGeometry args={[width, height, depth]} />
        <meshStandardMaterial
          color={colorString}
          roughness={0.3}
          metalness={0.0}
          envMapIntensity={0.5}
        />

        {group.bricks.map((brick) => {
          const isSelected = selectedBrickId === brick.id
          return (
            <React.Fragment key={brick.id}>
              <Instance
                position={[brick.position.x, brick.position.y, brick.position.z]}
                rotation={[0, (brick.rotation * Math.PI) / 180, 0]}
              />
            </React.Fragment>
          )
        })}
      </Instances>

      {/* Individual brick components for physics, studs, and interaction */}
      {group.bricks.map((brick) => (
        <IndividualBrickFeatures
          key={brick.id}
          brick={brick}
          width={width}
          height={height}
          depth={depth}
          colorString={colorString}
          selectedBrickId={selectedBrickId}
          setSelectedBrickId={setSelectedBrickId}
          debugMode={debugMode}
          physicsDisabled={physicsDisabled}
          onHover={onHover}
          onPlaceBrick={onPlaceBrick}
          onPointerDown={onPointerDown}
          onPointerMoveForDrag={onPointerMoveForDrag}
          isPreviewShowing={isPreviewShowing}
        />
      ))}
    </>
  )
}

/**
 * IndividualBrickFeatures Component
 *
 * Handles per-brick features that can't be instanced:
 * - Physics colliders (RigidBody)
 * - Studs and sockets
 * - Interaction events
 * - Selection highlighting
 */
interface IndividualBrickFeaturesProps {
  brick: Brick
  width: number
  height: number
  depth: number
  colorString: string
  selectedBrickId: string | null
  setSelectedBrickId: (id: string | null) => void
  debugMode: boolean
  physicsDisabled: boolean
  onHover?: (point: Vector3, isTopSurface: boolean, hoveredBrick?: Brick) => void
  onPlaceBrick?: (event: ThreeEvent<MouseEvent>) => void
  onPointerDown?: (event: ThreeEvent<PointerEvent>) => void
  onPointerMoveForDrag?: (event: ThreeEvent<PointerEvent>) => void
  isPreviewShowing?: boolean
}

const IndividualBrickFeaturesComponent: React.FC<IndividualBrickFeaturesProps> = ({
  brick,
  width,
  height,
  depth,
  colorString,
  selectedBrickId,
  setSelectedBrickId,
  debugMode,
  physicsDisabled,
  onHover,
  onPlaceBrick,
  onPointerDown,
  onPointerMoveForDrag,
  isPreviewShowing,
}) => {
  const isSelected = selectedBrickId === brick.id
  const [isHoveringTopSurface, setIsHoveringTopSurface] = React.useState(false)

  // Event handlers with useCallback for performance (Task 4.1.7)
  const handlePointerMove = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!onHover) return
      event.stopPropagation()

      if (onPointerMoveForDrag) {
        onPointerMoveForDrag(event)
      }

      const normal = event.face?.normal
      if (!normal) return

      const isTopSurface = normal.y > 0.9
      setIsHoveringTopSurface(isTopSurface)
      onHover(event.point.clone(), isTopSurface, brick)
    },
    [onHover, onPointerMoveForDrag, brick]
  )

  const handlePointerEnter = useCallback(
    (event: ThreeEvent<PointerEvent>) => {
      if (!onHover) return
      event.stopPropagation()

      const normal = event.face?.normal
      if (!normal) return

      const isTopSurface = normal.y > 0.9
      setIsHoveringTopSurface(isTopSurface)
      onHover(event.point.clone(), isTopSurface, brick)
    },
    [onHover, brick]
  )

  const handlePointerLeave = useCallback(
    (_event: ThreeEvent<PointerEvent>) => {
      setIsHoveringTopSurface(false)
    },
    []
  )

  const handleClick = useCallback(
    (event: ThreeEvent<MouseEvent>) => {
      event.stopPropagation()

      if (isHoveringTopSurface && isPreviewShowing && onPlaceBrick) {
        onPlaceBrick(event)
        return
      }

      if (isSelected) {
        setSelectedBrickId(null)
      } else {
        setSelectedBrickId(brick.id)
      }
    },
    [
      isHoveringTopSurface,
      isPreviewShowing,
      isSelected,
      onPlaceBrick,
      setSelectedBrickId,
      brick.id,
    ]
  )

  return (
    <>
      {/* Physics body for collision - Task 4.1.6: Conditionally disabled */}
      {physicsDisabled ? (
        // No physics mode: Just render studs/sockets at brick position
        <group
          position={[brick.position.x, brick.position.y, brick.position.z]}
          rotation={[0, (brick.rotation * Math.PI) / 180, 0]}
        >
          {/* Invisible mesh for studs and sockets */}
          <mesh visible={false}>
            <boxGeometry args={[width, height, depth]} />

            {/* Studs on top */}
            <BrickStuds type={brick.type} rotation={0} color={brick.color} />

            {/* Sockets on bottom */}
            <BrickSockets type={brick.type} rotation={0} color={brick.color} visible={false} />
          </mesh>
        </group>
      ) : (
        // Normal mode: Physics enabled
        <RigidBody
          type="fixed"
          position={[brick.position.x, brick.position.y, brick.position.z]}
          rotation={[0, (brick.rotation * Math.PI) / 180, 0]}
          colliders={false}
        >
          <CuboidCollider args={[width / 2, height / 2, depth / 2]} />

          {/* Invisible mesh for studs and sockets */}
          <mesh visible={false}>
            <boxGeometry args={[width, height, depth]} />

            {/* Studs on top */}
            <BrickStuds type={brick.type} rotation={0} color={brick.color} />

            {/* Sockets on bottom */}
            <BrickSockets type={brick.type} rotation={0} color={brick.color} visible={false} />
          </mesh>
        </RigidBody>
      )}

      {/* Invisible interaction mesh for pointer events */}
      <mesh
        position={[brick.position.x, brick.position.y, brick.position.z]}
        rotation={[0, (brick.rotation * Math.PI) / 180, 0]}
        onPointerDown={onPointerDown}
        onPointerEnter={handlePointerEnter}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        onClick={handleClick}
        visible={false}
      >
        <boxGeometry args={[width, height, depth]} />
        <meshBasicMaterial transparent opacity={0} />
      </mesh>

      {/* Selection highlight - emissive overlay */}
      {isSelected && (
        <mesh
          position={[brick.position.x, brick.position.y, brick.position.z]}
          rotation={[0, (brick.rotation * Math.PI) / 180, 0]}
        >
          <boxGeometry args={[width, height, depth]} />
          <meshStandardMaterial
            color={colorString}
            roughness={0.3}
            metalness={0.0}
            envMapIntensity={0.5}
            emissive="#4299e1"
            emissiveIntensity={0.3}
            transparent
            opacity={1}
          />

          {/* Studs on top for selected brick */}
          <BrickStuds type={brick.type} rotation={0} color={brick.color} />
        </mesh>
      )}

      {/* Selection outline (debug mode only) */}
      {isSelected && debugMode && (
        <mesh
          position={[brick.position.x, brick.position.y, brick.position.z]}
          rotation={[0, (brick.rotation * Math.PI) / 180, 0]}
        >
          <boxGeometry args={[width * 1.05, height * 1.05, depth * 1.05]} />
          <meshBasicMaterial color="#4299e1" wireframe transparent opacity={0.5} />
        </mesh>
      )}
    </>
  )
}

/**
 * Memoized IndividualBrickFeatures Component
 *
 * Task 4.1.7 - React Performance Optimization
 *
 * Wraps IndividualBrickFeaturesComponent with React.memo and custom comparison function
 * to prevent unnecessary re-renders when selection changes don't affect this brick.
 *
 * Performance Impact:
 * - Before: 10,000 component re-renders on selection change
 * - After: 2 component re-renders on selection change (old + new selection)
 * - Improvement: 99.98% reduction in re-renders for selection changes
 */
const IndividualBrickFeatures = React.memo(
  IndividualBrickFeaturesComponent,
  (prevProps, nextProps) => {
    // Only re-render if THIS brick is affected by changes

    // Check if this brick's selection state changed
    const wasSelected = prevProps.selectedBrickId === prevProps.brick.id
    const isSelected = nextProps.selectedBrickId === nextProps.brick.id
    const selectionChanged = wasSelected !== isSelected

    // Check if brick data changed
    const brickChanged = prevProps.brick !== nextProps.brick

    // Check if preview state changed (affects all bricks)
    const previewChanged = prevProps.isPreviewShowing !== nextProps.isPreviewShowing

    // Check if debug/physics mode changed (rare)
    const modeChanged =
      prevProps.debugMode !== nextProps.debugMode ||
      prevProps.physicsDisabled !== nextProps.physicsDisabled

    // Return true to SKIP re-render (props are equal)
    // Return false to ALLOW re-render (props changed)
    return !(selectionChanged || brickChanged || previewChanged || modeChanged)
  }
)
